import { useApolloClient } from '@ts-gql/apollo';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';

const SEARCH_JOBS_AUTOCOMPLETE = gql`
  query SearchJobsAutocomplete(
    $text: String
    $myWork: Boolean
    $statusIds: [String!]
    $take: Float
  ) {
    searchJobs(
      text: $text
      myWork: $myWork
      statusIds: $statusIds
      take: $take
    ) {
      cloudId
      name
      clientId
    }
  }
` as import('../../__generated__/ts-gql/SearchJobsAutocomplete').type;
export type SearchJobsAutocompleteVariables = OperationVariables<
  typeof SEARCH_JOBS_AUTOCOMPLETE
>;
export type SearchJobsAutocompleteResult = OperationData<
  typeof SEARCH_JOBS_AUTOCOMPLETE
>;

const GET_CLIENTS_BY_IDS_JOB_AUTOCOMPLETE = gql`
  query GetClientsByIdsJobAutocomplete($cloudIds: [String!]!) {
    getClientsByIds(cloudIds: $cloudIds) {
      cloudId
      name
    }
  }
` as import('../../__generated__/ts-gql/GetClientsByIdsJobAutocomplete').type;
export type GetClientsByIdsJobAutocompleteVariables = OperationVariables<
  typeof GET_CLIENTS_BY_IDS_JOB_AUTOCOMPLETE
>;
export type GetClientsByIdsJobAutocompleteResult = OperationData<
  typeof GET_CLIENTS_BY_IDS_JOB_AUTOCOMPLETE
>;

export type JobAutoCompleteResult = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useJobAutoComplete = () => {
  const client = useApolloClient();

  // This is the easy to call function we can await that we need to return
  // for this usage, we have to have a promise we can await. We can't do renderloop and useeffect and usememo etc
  // we absolutely need something we can await, lightly transform the results for display, and hand back to the autocomplete
  const doSearch = async (text: string, statusIds?: string[]) => {
    const searchJobsAutocompleteOptions = {
      query: SEARCH_JOBS_AUTOCOMPLETE,
      variables: {
        text,
        myWork: false,
        statusIds: statusIds || null,
        take: 20,
      },
      context: { service: 'core' },
    };
    const jobs = await client.query<typeof SEARCH_JOBS_AUTOCOMPLETE>(
      searchJobsAutocompleteOptions
    );
    if (jobs.errors) {
      return { error: true };
    }
    if (!jobs.data?.searchJobs?.length) {
      return { data: [] };
    }

    const jobsData = jobs.data.searchJobs || [];

    // TODO make these unique
    const clientIds = jobsData
      .map((j) => j.clientId)
      .filter((value): value is string => !!value);
    const getClientsByIdsJobAutocompleteOptions = {
      query: GET_CLIENTS_BY_IDS_JOB_AUTOCOMPLETE,
      variables: { cloudIds: clientIds },
      context: { service: 'clients' },
    };
    const clients = clientIds.length
      ? await client.query<typeof GET_CLIENTS_BY_IDS_JOB_AUTOCOMPLETE>(
          getClientsByIdsJobAutocompleteOptions
        )
      : undefined;
    if (clients?.errors) {
      return { error: true };
    }

    const clientsData = clients?.data?.getClientsByIds || [];

    // match the jobs up with their clients
    // rough, could do a prettier job with the results, but this works
    return { data: processData(jobsData, clientsData) };
  };
  return doSearch;
};

const processData = (
  jobsData: NonNullable<SearchJobsAutocompleteResult['searchJobs']>,
  clientsData: NonNullable<
    GetClientsByIdsJobAutocompleteResult['getClientsByIds']
  >
) => {
  const clientMap = new Map(clientsData.map((c) => [c.cloudId, c]));
  return jobsData.map((j) => {
    return { job: j, client: clientMap.get(j.clientId || '') };
  });
};
